import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getBarAvailabilityApi, holdReservationApi } from "../../../api/api";
import { deriveFittingZones, normalizeAvailability, validatePartySize } from "./quickReservationModel";
export default function useQuickTableReservation({ selection, token, onHandoff, onRequireLogin }) {
  const [availability, setAvailability] = useState(null);
  const [status, setStatus] = useState("loading");
  const [notice, setNotice] = useState("");
  const [partyInput, setPartyInput] = useState("2");
  const [selectedZone, setSelectedZone] = useState("");
  const generation = useRef(0);
  const holdPromise = useRef(null);
  const party = validatePartySize(partyInput, selection?.bar?.maxPartySize);
  const zones = useMemo(() => deriveFittingZones(availability, selection?.slot, party.value, selection?.bar),
    [availability, selection, party.value]);
  const load = useCallback(async () => {
    const own = ++generation.current;
    setStatus("loading");
    setSelectedZone("");
    try {
      const response = await getBarAvailabilityApi(selection.bar._id, selection.date);
      if (own !== generation.current) return;
      const normalized = normalizeAvailability(response);
      setAvailability(normalized);
      setStatus(normalized ? "ready" : "error");
      if (!normalized) setNotice("No pudimos validar la disponibilidad. Intentá de nuevo.");
    } catch (error) {
      if (own === generation.current) {
        setAvailability(null);
        setStatus("error");
        setNotice(error?.response?.data?.message || "No pudimos cargar la disponibilidad. Intentá de nuevo.");
      }
    }
  }, [selection]);
  useEffect(() => { load(); return () => { generation.current += 1; }; }, [load]);
  useEffect(() => {
    if (zones.length === 1) setSelectedZone(zones[0].name);
    else if (!zones.some((zone) => zone.name === selectedZone)) setSelectedZone("");
  }, [zones, selectedZone]);
  const confirm = async () => {
    if (!party.valid || !selectedZone || holdPromise.current) return;
    const pending = { ...selection, partySize: party.value, selectedZone };
    if (!token) return onRequireLogin?.(pending);
    const request = holdReservationApi({ barId: selection.bar._id, zone: selectedZone, date: selection.date,
      slotStart: selection.slot.slotStart, partySize: party.value }, token);
    holdPromise.current = request;
    setStatus("holding");
    try {
      const response = await request;
      const info = response?.data?.info;
      if (!info?.holdId || !info?.expiresAt) throw new Error("malformed hold");
      setStatus("handoff");
      onHandoff?.({ ...pending, holdId: info.holdId, holdExpiresAt: info.expiresAt });
    } catch (error) {
      if (error?.response?.status === 401) return onRequireLogin?.(pending);
      if (error?.response?.status === 409) {
        setNotice("Ese sector se ocupó. Actualizamos la disponibilidad.");
        await load();
      } else {
        setStatus("error");
        setNotice(error?.response?.data?.message || "No pudimos bloquear el lugar. Intentá de nuevo.");
      }
    } finally {
      holdPromise.current = null;
    }
  };
  return { status, notice, partyInput, setPartyInput, party, zones, selectedZone, setSelectedZone, load, confirm };
}
